/**
 * Shared helper UI building blocks for lightweight admin P1 modules.
 * All components match the dark zinc aesthetic of the rest of the admin.
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, Pencil, Save, X, Search } from 'lucide-react';
import { useAdmin } from '@/hooks/useAdmin';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AdminPageHeader({ icon: Icon, title, subtitle, children, testid }) {
  return (
    <div className="p-4 md:p-6" data-testid={testid}>
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div className="flex items-center gap-3">
          {Icon && <Icon className="text-red-500" size={28} />}
          <div>
            <h1 className="text-2xl font-bold">{title}</h1>
            {subtitle && <p className="text-sm text-zinc-400">{subtitle}</p>}
          </div>
        </div>
        <div className="flex items-center gap-2">{children}</div>
      </div>
    </div>
  );
}

export function StatCard({ label, value, accent = 'red', testid }) {
  const colours = {
    red: 'text-red-400',
    green: 'text-emerald-400',
    amber: 'text-amber-400',
    blue: 'text-sky-400',
    violet: 'text-violet-400',
  };
  return (
    <div data-testid={testid}
         className="border border-zinc-800 bg-zinc-900/50 rounded-lg p-4">
      <div className="text-xs uppercase tracking-wider text-zinc-500">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${colours[accent] || ''}`}>{value}</div>
    </div>
  );
}

/**
 * Generic admin list/CRUD page for any collection served by /api/admin/:path.
 * `fields` describes form rows; keep it simple.
 */
export function CollectionAdmin({ path, title, subtitle, icon, fields, testid, renderRow }) {
  const { authFetch } = useAdmin();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {...}

  const load = async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${API}/admin/${path}?q=${encodeURIComponent(q)}`);
      const j = await r.json();
      setItems(j.items || []);
      setTotal(j.total || 0);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    const isNew = !editing?.id;
    const url = isNew ? `${API}/admin/${path}` : `${API}/admin/${path}/${editing.id}`;
    const method = isNew ? 'POST' : 'PUT';
    const body = { data: { ...editing } };
    const r = await authFetch(url, {
      method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    });
    if (r.ok) { toast.success('Spremljeno'); setEditing(null); load(); }
    else toast.error((await r.json()).detail || 'Greška');
  };

  const del = async (id) => {
    if (!window.confirm('Izbrisati ovu stavku?')) return;
    const r = await authFetch(`${API}/admin/${path}/${id}`, { method: 'DELETE' });
    if (r.ok) { toast.success('Izbrisano'); load(); }
  };

  return (
    <div data-testid={testid || `admin-${path}`}>
      <AdminPageHeader icon={icon} title={title} subtitle={subtitle ? `${subtitle} · Ukupno: ${total}` : `Ukupno: ${total}`}>
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Pretraži…"
                   data-testid={`${path}-search`}
                   className="bg-zinc-900 border border-zinc-800 rounded pl-8 pr-3 py-2 text-sm" />
          </div>
          <button data-testid={`${path}-new-btn`} type="button"
                  onClick={() => setEditing({})}
                  className="inline-flex items-center gap-1 px-3 py-2 rounded bg-red-600 hover:bg-red-700 text-white text-sm font-bold">
            <Plus size={14}/> Nova
          </button>
        </form>
      </AdminPageHeader>
      <div className="px-4 md:px-6 pb-6">
        {loading ? <Loader2 className="animate-spin text-zinc-500"/> : (
          <div className="overflow-x-auto rounded-lg border border-zinc-800">
            <table className="min-w-full text-sm">
              <thead className="bg-zinc-900 text-xs text-zinc-400 uppercase">
                <tr>
                  {fields.map(f => <th key={f.key} className="px-4 py-2 text-left">{f.label}</th>)}
                  <th className="px-4 py-2 text-right">Akcije</th>
                </tr>
              </thead>
              <tbody>
                {items.map(it => (
                  <tr key={it.id} data-testid={`${path}-row-${it.id}`} className="border-t border-zinc-800 hover:bg-zinc-900/50">
                    {renderRow
                      ? renderRow(it)
                      : fields.map(f => <td key={f.key} className="px-4 py-2 break-words max-w-[320px]">{String(it[f.key] ?? '—')}</td>)}
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      <button onClick={() => setEditing({ ...it })} data-testid={`${path}-edit-${it.id}`}
                              className="p-1.5 rounded bg-zinc-700 hover:bg-zinc-600 text-white mr-1"><Pencil size={14}/></button>
                      <button onClick={() => del(it.id)} data-testid={`${path}-delete-${it.id}`}
                              className="p-1.5 rounded bg-red-800 hover:bg-red-700 text-white"><Trash2 size={14}/></button>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr><td colSpan={fields.length + 1} className="px-4 py-8 text-center text-zinc-500">Nema podataka.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {editing !== null && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
             onClick={() => setEditing(null)}>
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg w-full max-w-xl p-5" onClick={(e) => e.stopPropagation()}
               data-testid={`${path}-edit-modal`}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">{editing.id ? 'Uredi' : 'Nova stavka'}</h2>
              <button onClick={() => setEditing(null)} className="text-zinc-400 hover:text-white"><X size={18}/></button>
            </div>
            <div className="space-y-3">
              {fields.map(f => (
                <div key={f.key}>
                  <label className="text-xs uppercase text-zinc-500 block mb-1">{f.label}</label>
                  {f.type === 'textarea' ? (
                    <textarea value={editing[f.key] ?? ''}
                              data-testid={`${path}-field-${f.key}`}
                              onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })}
                              className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm h-24" />
                  ) : f.type === 'number' ? (
                    <input type="number" value={editing[f.key] ?? ''}
                           data-testid={`${path}-field-${f.key}`}
                           onChange={(e) => setEditing({ ...editing, [f.key]: Number(e.target.value) })}
                           className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm" />
                  ) : f.type === 'checkbox' ? (
                    <label className="inline-flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={!!editing[f.key]}
                             data-testid={`${path}-field-${f.key}`}
                             onChange={(e) => setEditing({ ...editing, [f.key]: e.target.checked })} />
                      {f.label}
                    </label>
                  ) : (
                    <input value={editing[f.key] ?? ''}
                           data-testid={`${path}-field-${f.key}`}
                           onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })}
                           className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm" />
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setEditing(null)} className="px-3 py-2 rounded bg-zinc-800 hover:bg-zinc-700 text-sm">Odustani</button>
              <button onClick={save} data-testid={`${path}-save-btn`}
                      className="px-3 py-2 rounded bg-red-600 hover:bg-red-700 text-white text-sm font-bold inline-flex items-center gap-1">
                <Save size={14}/> Spremi
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function useFetcher(path, deps = []) {
  const { authFetch } = useAdmin();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const reload = async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${API}/admin/${path}`);
      setData(await r.json());
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, deps);
  return { data, loading, reload };
}

export { API };
